import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AuthContext from './AuthContext';
import { getMyCapabilities } from 'Services/userService';

const CapabilityContext = createContext();

export default CapabilityContext;

/**
 * Holds what the logged-in user's role may do in the portal.
 *
 * Modelled on EntitlementContext: the login response seeds it for a fast first
 * paint, then `GET /me/capabilities/` confirms it on every app load so a role
 * change made by a store admin lands on the teammate's next page load, without
 * a re-login. Gates stay closed until that confirmation arrives.
 *
 * Nothing here knows a role by number. A gate asks whether a capability code is
 * held, and the answer comes from the backend, so changing the access matrix
 * needs no frontend change.
 */

export const CapabilityProvider = ({ children }) => {
  const { user, loggedIn } = useContext(AuthContext);

  const [capabilities, setCapabilities] = useState(() => new Set(user?.capabilities || []));
  const [assignableRoles, setAssignableRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  // Gates stay closed until capabilities have been confirmed against the API.
  // Without this a cached login payload could flash controls the role no
  // longer has, or redirect a user off a page they can in fact reach.
  const [isResolved, setIsResolved] = useState(false);

  const userId = user?.uid;

  /**
   * Re-read capabilities from the API.
   *
   * @returns {void}
   */
  const refreshCapabilities = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getMyCapabilities();
      const data = res?.data?.data;

      if (data) {
        setCapabilities(new Set(data.capabilities || []));
        setAssignableRoles(data.assignable_roles || []);
      }
    } catch (error) {
      // A failed refresh leaves the seeded capabilities in place rather than
      // blanking the menu; the backend still enforces every request.
      console.error('Failed to refresh capabilities', error);
    } finally {
      setLoading(false);
      setIsResolved(true);
    }
  }, []);

  useEffect(() => {
    if (!loggedIn || !userId) {
      setCapabilities(new Set());
      setAssignableRoles([]);
      setIsResolved(true);
      return;
    }

    // Seed from the login payload, then confirm.
    setCapabilities(new Set(user?.capabilities || []));
    setIsResolved(false);
    refreshCapabilities();
    // `user.capabilities` is written once per login alongside `uid`, so the
    // user id is the right trigger; depending on the array would refire on
    // every unrelated user-object update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, userId, refreshCapabilities]);

  /**
   * Whether the user's role holds a capability.
   *
   * @param {String} capability capability code, e.g. 'hosts.manage'
   * @returns {Boolean}
   */
  const can = useCallback((capability) => capabilities.has(capability), [capabilities]);

  /**
   * Whether the user's role holds at least one of several capabilities.
   *
   * @param {String[]} codes capability codes
   * @returns {Boolean}
   */
  const canAny = useCallback(
    (codes) => (codes || []).some((code) => capabilities.has(code)),
    [capabilities]
  );

  // A store-side user is whoever holds at least one store capability. This
  // replaces the scattered `role === 2 || 4 || 5 || 6` checks: a new store
  // role is store-side the moment the backend grants it anything.
  const isStoreUser = capabilities.size > 0;

  const contextData = useMemo(
    () => ({
      capabilities,
      assignableRoles,
      loading,
      isResolved,
      isStoreUser,
      can,
      canAny,
      refreshCapabilities,
    }),
    [capabilities, assignableRoles, loading, isResolved, isStoreUser, can, canAny, refreshCapabilities]
  );

  return (
    <CapabilityContext.Provider value={contextData}>
      {children}
    </CapabilityContext.Provider>
  );
};
